import Loading from "components/Loading";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect, useState } from "react";
import { FaBriefcase } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { fetchFeedback } from "services/complaintDetailService";
import { Feedback } from "types";
import { DateView, errorHandler } from "utils/helper";
import PageHeader from "../../components/PageHeader";

const tabs = [
  "All",
  "Excellent",
  "Very Good",
  "Good",
  "Satisfactory",
  "UnSatisfactory",
];

// Helper function to convert numeric rating to text rating
const getRatingText = (rating: string | number | undefined): string => {
  const numRating = Number(rating);
  switch (numRating) {
    case 1:
      return "UnSatisfactory";
    case 2:
      return "Satisfactory";
    case 3:
      return "Good";
    case 4:
      return "Very Good";
    case 5:
      return "Excellent";
    default:
      return "N/A";
  }
};

const FeedbackList: React.FC = () => {
  const { showTooltip } = useTooltip();
  const { loginData } = useAuth();
  const [activeTab, setActiveTab] = useState("All");
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [selectedMessage, setSelectedMessage] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchData = () => {
    setLoading(true);
    const org_unit_id = loginData?.org_unit?.toString();
    const admin_unit_id = loginData?.admin_unit?.toString();

    fetchFeedback(org_unit_id, admin_unit_id)
      .then((data) => {
        // Sort feedback by date in descending order (newest first)
        const sortedData = data.sort((a, b) => {
          const dateA = new Date(a.created_at || 0).getTime();
          const dateB = new Date(b.created_at || 0).getTime();
          return dateB - dateA; // Descending order
        });
        setFeedbacks(sortedData);
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(errorHandler(error));
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const searchTerm = search.trim().toLowerCase();
  const filtered = feedbacks.filter((feedback) => {
    const id = feedback?.id?.toString()?.toLowerCase() || "";
    const feedbackNumber =
      feedback?.feedback_number?.toString()?.toLowerCase() || "";
    const user = feedback?.user?.toLowerCase() || "";
    const message = feedback?.message?.toString()?.toLowerCase() || "";
    const rating = feedback?.rating?.toString() || "";

    const matchesText =
      id.includes(searchTerm) ||
      feedbackNumber.includes(searchTerm) ||
      user.includes(searchTerm) ||
      message.includes(searchTerm) ||
      rating.includes(searchTerm);

    if (activeTab !== "All") {
      const ratingText = getRatingText(feedback?.rating);
      return matchesText && ratingText === activeTab;
    }

    return matchesText;
  });

  return (
    <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
      <PageHeader
        title="Feedback"
        icon={FaBriefcase}
        tabs={tabs}
        activeTab={activeTab}
        onTabClick={setActiveTab}
        rightContent={<></>}
      />

      <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
        <div className="overflow-hidden bg-white shadow rounded-xl">
          {loading ? (
            <Loading label="Loading Feedback" />
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead className="text-white bg-black">
                <tr>
                  <th colSpan={7} className="p-4 rounded-t-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-lg font-semibold">
                        <button
                          onClick={() => navigate(-1)}
                          className="text-white hover:text-gray-300"
                          title="Go Back"
                        >
                          ◀️
                        </button>
                        <span>Feedback Table</span>
                      </div>
                      <div className="relative w-80">
                        <span className="absolute inset-y-0 flex items-center text-gray-500 pointer-events-none left-3">
                          🔍
                        </span>
                        <input
                          type="text"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder="Search"
                          className="w-full py-2 pl-10 pr-4 text-sm text-black placeholder-gray-600 bg-gray-200 rounded-md focus:outline-none"
                        />
                      </div>
                    </div>
                  </th>
                </tr>
                <tr>
                  <th className="p-3 text-left">Feedback No.</th>
                  <th className="p-3 text-left">Client Name</th>
                  <th className="p-3 text-left">Rating</th>
                  <th className="p-3 text-left">Date</th>
                  <th className="p-3 text-left">Service App No.</th>
                  <th className="p-3 text-left">Message</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length > 0 ? (
                  filtered.map((data, idx) => (
                    <tr
                      key={idx}
                      className={idx % 2 === 0 ? "bg-[#F7F4F1AB]" : "bg-white"}
                    >
                      <td className="p-3 font-[500]">
                        {data?.feedback_number || "N/A"}
                      </td>
                      <td className="p-3 font-[500]">{data?.user}</td>
                      <td className="p-3 font-[500]">
                        <span
                          className={
                            data?.rating === 5
                              ? "bg-[#4CAF50] text-white px-2 py-1 rounded-md"
                              : ""
                          }
                        >
                          {getRatingText(data?.rating)}
                        </span>
                      </td>
                      <td className="p-3 font-[500]">
                        {DateView(data.created_at) || "N/A"}
                      </td>
                      <td className="p-3 font-[500]">
                        {data?.service_application_number || "N/A"}
                      </td>
                      <td className="p-3 font-[500]">
                        {data?.message ? (
                          <button
                            onClick={() => setSelectedMessage(data.message)}
                            className="text-red-800 hover:text-red-900 underline cursor-pointer"
                          >
                            View Message
                          </button>
                        ) : (
                          "No message"
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="p-4 text-center text-gray-500">
                      No feedback found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Message Popup Modal */}
      {selectedMessage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-75"
          onClick={() => setSelectedMessage(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="text-2xl">💬</div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Feedback Message
                  </h2>
                </div>
              </div>
              <button
                onClick={() => setSelectedMessage(null)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                aria-label="Close modal"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-auto p-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-800 whitespace-pre-wrap text-sm leading-relaxed">
                  {selectedMessage}
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-end">
                <button
                  onClick={() => setSelectedMessage(null)}
                  className="px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackList;
